"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { apiFetch } from "@/lib/api/apiFetch"
import { setAccessToken } from "@/lib/api/authTokens"
import { triggerLogout } from "@/lib/auth/authEvents"
import { startAuthentication } from "@simplewebauthn/browser"
import { toast } from "sonner"

/**
 * useAuth hook
 * 
 * Provides centralized authentication handlers for login, logout, and registration.
 * Manages loading and error states for authentication actions.
 * 
 * @returns {Object} Authentication handlers and state
 */
export function useAuth() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const router = useRouter()

    /**
     * Handles user login
     * 
     * @param {Object} credentials - Login credentials
     * @param {string} credentials.email - User email
     * @param {string} credentials.password - User password
     * @returns {Promise<boolean>} Success status
     */
    const loginHandler = async ({ email, password }: any) => {
        setLoading(true)
        setError(null)
        try {
            const result = await fetch(`/api/auth/login`, {
                method: "POST",
                body: JSON.stringify({ email, password }),
            })

            const data = await result.json()

            if (data?.error) {
                setError(data.message || "Invalid credentials")
                return { success: false }
            }

            // Check if MFA is required
            if (data.code === "MFA_VERIFICATION_REQUIRED") {
                return { 
                    success: true, 
                    mfaRequired: true, 
                    mfaData: data.data // Contains userId, preferredMFAMethod, and mfa.methods
                }
            }

            await setAccessToken(data.data.accessToken)

            // Fetch user details to determine role-based redirection
            try {
                const userDetails = await apiFetch("/api/users/details");
                if (userDetails?.data?.role === "superAdmin") {
                    router.replace("/analytics");
                } else {
                    router.replace("/contacts");
                }
            } catch (fetchError) {
                console.error("Failed to fetch user details for redirection:", fetchError);
                toast.error("Failed to fetch user details for redirection");
                //router.replace("/contacts"); // Fallback to contacts
            }

            return { success: true, mfaRequired: false }
        } catch (err: any) {
            setError(err.message || "An error occurred during login")
            return { success: false }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Handles secondary MFA verification during login.
     * 
     * @param {Object} payload 
     * @param {string} payload.userId - The user ID received from login
     * @param {string} payload.method - The MFA method used ("totp" | "backupCodes")
     * @param {string} payload.token - The OTP or backup code
     */
    const mfaVerifyHandler = async ({ userId, method, token }: any) => {
        setLoading(true)
        setError(null)
        try {
            const result = await fetch(`/api/auth/mfa-verify`, {
                method: "POST",
                body: JSON.stringify({ userId, method, token }),
            })
            
            const data = await result.json()

            if (data?.error) {
                setError(data.message || "Invalid authentication code")
                return false
            }

            await setAccessToken(data.data.accessToken)

            // Fetch user details to determine role-based redirection
            try {
                const userDetails = await apiFetch("/api/users/details");
                if (userDetails?.data?.role === "superAdmin") {
                    router.replace("/analytics");
                } else {
                    router.replace("/contacts");
                }
            } catch (fetchError) {
                console.error("Failed to fetch user details for redirection:", fetchError);
                toast.error("Failed to fetch user details for redirection");
            }

            return true
        } catch (err: any) {
            setError(err.message || "An error occurred during verification")
            return false
        } finally {
            setLoading(false)
        }
    }

    /**
     * Handles passkey authentication during login or MFA step.
     * 
     * @param {string} userId - The user ID to authenticate
     */
    const passkeyAuthHandler = async (userId: string) => {
        setLoading(true)
        setError(null)
        try {
            // 1. Get authentication options
            const optionsRes = await fetch(`/api/auth/passkey/login`, {
                method: "POST",
                body: JSON.stringify({ userID: userId }),
            })
            const optionsData = await optionsRes.json()
            
            if (optionsData?.error) {
                setError(optionsData.message || "Failed to initiate passkey login")
                return false
            }

            // 2. Instruct browser to authenticate
            let authenticationResponse
            try {
                authenticationResponse = await startAuthentication(optionsData.data.options)
            } catch (error: any) {
                setError("Authentication cancelled or not authorized")
                return false
            }

            // 3. Verify authentication with backend
            const verifyRes = await fetch(`/api/auth/passkey/verify`, {
                method: "POST",
                body: JSON.stringify({ userId, authenticationResponse }),
            })
            const verifyData = await verifyRes.json()

            if (verifyData?.error) {
                setError(verifyData.message || "Passkey verification failed")
                return false
            }

            await setAccessToken(verifyData.data.accessToken)

            // Fetch user details for redirection
            try {
                const userDetails = await apiFetch("/api/users/details");
                if (userDetails?.data?.role === "superAdmin") {
                    router.replace("/analytics");
                } else {
                    router.replace("/contacts");
                }
            } catch (err) {
                toast.error("Failed to fetch user details");
            }
            return true
            
        } catch (err: any) {
            setError(err.message || "An error occurred with passkey login")
            return false
        } finally {
            setLoading(false)
        }
    }

    /**
     * Handles user registration
     * 
     * @param {Object} userData - Registration data
     * @param {string} userData.name - User full name
     * @param {string} userData.email - User email
     * @param {string} userData.password - User password
     * @returns {Promise<boolean>} Success status
     */
    const registerHandler = async ({ name, email, password }: any) => {
        setLoading(true)
        setError(null)
        try {
            const data = await apiFetch("/api/users/create", {
                method: "POST",
                data: { name, email, password },
            })

            if (data?.error) {
                setError(data.message || "Registration failed")
                return false
            }

            router.replace("/login")
            return true
        } catch (err: any) {
            setError(err.message || "An error occurred during registration")
            return false
        } finally {
            setLoading(false)
        }
    }

    /**
     * Handles user logout
     * 
     * Clears access token and redirects to login page.
     * @returns {Promise<void>}
     */
    const logoutHandler = async () => {
        setLoading(true)
        try {
            apiFetch("/api/auth/logout", {
                method: "POST",
            })
        } catch (err) {
            console.error("Logout failed:", err)
        } finally {
            setAccessToken(null)
            setLoading(false)
            router.replace("/login")
        }
    }

    return {
        loginHandler,
        logoutHandler,
        registerHandler,
        mfaVerifyHandler,
        passkeyAuthHandler,
        loading,
        error,
        setError
    }
}
